import type { FormFieldType, FormPathType } from '@engage/types';
import { Icon, ICONS } from '../ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../ui/tokens';

interface Item {
  type: FormFieldType;
  label: string;
}

interface PresetItem {
  type: FormFieldType;
  /** Canonical key the submission maps to a contact field by. Must be exact. */
  key: string;
  label: string;
}

// Pre-mapped contact fields. Clicking one adds a field with the exact key the
// backend reads when creating/updating a contact from a submission — so the
// recruiter doesn't have to know the key conventions (first_name, last_name…).
const CONTACT_KEY_FIELDS: PresetItem[] = [
  { type: 'SHORT_TEXT', key: 'first_name', label: 'First name' },
  { type: 'SHORT_TEXT', key: 'last_name',  label: 'Surname' },
  { type: 'EMAIL',      key: 'email',      label: 'Email' },
  { type: 'PHONE',      key: 'phone',      label: 'Phone' },
  { type: 'SHORT_TEXT', key: 'company',    label: 'Company' },
  { type: 'SHORT_TEXT', key: 'title',      label: 'Job title' },
  { type: 'SHORT_TEXT', key: 'location',   label: 'Location' },
];

const COMMON: Item[] = [
  { type: 'SHORT_TEXT', label: 'Short text' },
  { type: 'LONG_TEXT', label: 'Long text' },
  { type: 'EMAIL', label: 'Email' },
  { type: 'PHONE', label: 'Phone' },
  { type: 'NUMBER', label: 'Number' },
  { type: 'DATE', label: 'Date' },
];

const PROFILE: Item[] = [
  { type: 'SINGLE_SELECT', label: 'Single select' },
  { type: 'MULTI_SELECT', label: 'Multi select' },
  { type: 'ADDRESS', label: 'Address' },
  { type: 'FILE_UPLOAD', label: 'File upload' },
];

const PRIVACY: Item[] = [
  { type: 'CONSENT_CHECKBOX', label: 'Consent checkbox' },
];

export function FieldPalette(props: {
  pathType: FormPathType;
  onAdd: (type: FormFieldType) => void;
  onAddPreset: (preset: PresetItem) => void;
  /** Keys already on the form — used to mark a preset as added. */
  existingKeys: string[];
}) {
  return (
    <div style={{ padding: '16px 14px', fontFamily: FORM_UI_FONT }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: C.faint,
          letterSpacing: 0.4,
          marginBottom: 8,
        }}
      >
        FIELD PALETTE
      </div>
      {/* Contact key fields first — these auto-map to the new contact. Shown on
          contact-path forms, where submissions create/append a contact. */}
      {props.pathType === 'CONTACT' && (
        <PresetSection
          title="CONTACT FIELDS"
          items={CONTACT_KEY_FIELDS}
          existingKeys={props.existingKeys}
          onAdd={props.onAddPreset}
        />
      )}
      <Section title="COMMON" items={COMMON} onAdd={props.onAdd} />
      <Section
        title={props.pathType === 'CANDIDATE' ? 'PROFILE' : 'MORE'}
        items={PROFILE}
        onAdd={props.onAdd}
      />
      <Section title="PRIVACY" items={PRIVACY} onAdd={props.onAdd} />
    </div>
  );
}

function PresetSection(props: {
  title: string;
  items: PresetItem[];
  existingKeys: string[];
  onAdd: (preset: PresetItem) => void;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: C.indigoSoft, margin: '6px 4px', letterSpacing: 0.3 }}>
        {props.title}
      </div>
      <div style={{ fontSize: 10.5, color: C.faint, margin: '0 4px 6px', lineHeight: 1.4 }}>
        Maps straight to the new contact.
      </div>
      {props.items.map((item) => {
        const added = props.existingKeys.includes(item.key);
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => props.onAdd(item)}
            disabled={added}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 9,
              padding: '8px 10px',
              borderRadius: 8,
              fontSize: 12.5,
              fontWeight: 500,
              color: added ? C.faint : C.ink,
              cursor: added ? 'default' : 'pointer',
              border: `1px solid ${added ? C.line : C.teal}`,
              marginBottom: 5,
              background: added ? C.bg : '#fff',
              fontFamily: FORM_UI_FONT,
              textAlign: 'left',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              <Icon d={added ? ICONS.check : ICONS.plus} size={13} color={added ? C.faint : C.teal} sw={2.4} />
              {item.label}
            </span>
            {added && <span style={{ fontSize: 10.5 }}>Added</span>}
          </button>
        );
      })}
    </div>
  );
}

function Section(props: {
  title: string;
  items: Item[];
  onAdd: (type: FormFieldType) => void;
}) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 700,
          color: C.indigoSoft,
          margin: '6px 4px',
          letterSpacing: 0.3,
        }}
      >
        {props.title}
      </div>
      {props.items.map((item) => (
        <button
          key={item.type}
          type="button"
          onClick={() => props.onAdd(item.type)}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '8px 10px',
            borderRadius: 8,
            fontSize: 12.5,
            fontWeight: 500,
            color: C.ink,
            cursor: 'pointer',
            border: `1px solid ${C.line}`,
            marginBottom: 5,
            background: '#fff',
            fontFamily: FORM_UI_FONT,
            textAlign: 'left',
          }}
        >
          <Icon d={ICONS.plus} size={13} color={C.teal} sw={2.4} />
          {item.label}
        </button>
      ))}
    </div>
  );
}
